import isEqual from 'lodash/isEqual';

import { generateDefaultValueForAttribute, validateAndRepairAttributeValue } from './attribute';
import { allSpecs } from './specs';
import type {
	ValidatorSpec,
	ValidatorSpecAttributes,
	ValidatorSpecAttributesGroup,
	ValidatorSpecNode,
	ValidatorSpecNodeExtends,
} from './specs/types';
import type { AdfAttrs } from './types';

function collectAttrNames(attrs: ValidatorSpecAttributes | undefined, names: Set<string>): void {
	if (!attrs) {
		return;
	}
	const groups = Array.isArray(attrs) ? attrs : [attrs];
	for (const group of groups) {
		for (const key of Object.keys(group.props)) {
			names.add(key);
		}
	}
}

let cachedAttributeNames: Set<string> | undefined;

/**
 * Every attribute name that appears on any node or mark in the validator
 * spec — i.e. a real, schema-owned ADF attribute name, not just a string
 * that happens to look like one. Computed once and cached; the spec table
 * is static for the lifetime of the process.
 */
export function getAllKnownAttributeNames(): Set<string> {
	if (cachedAttributeNames) {
		return cachedAttributeNames;
	}
	const names = new Set<string>();
	for (const spec of Object.values(allSpecs) as ValidatorSpec[]) {
		if (Array.isArray(spec)) {
			// ValidatorSpecNodeExtends is `[nodeType, ValidatorSpecNode]`.
			// ValidatorSpecGroup is `string[]` (just an alias list, no attrs).
			const [, extendedSpec] = spec as ValidatorSpecNodeExtends;
			if (typeof extendedSpec === 'object' && extendedSpec !== null) {
				collectAttrNames(extendedSpec.props?.attrs, names);
			}
			continue;
		}
		collectAttrNames(spec.props?.attrs, names);
	}
	cachedAttributeNames = names;
	return names;
}

/**
 * Resolve the actual node spec from a nodeType, handling secondary specs
 */
function resolveNodeSpec(nodeType: string): ValidatorSpecNode | null {
	const mainSpec = allSpecs[nodeType];
	if (!mainSpec) {
		return null;
	}

	const nodeSpec = mainSpec as ValidatorSpecNode;

	// If main spec has attrs, use it directly
	if (nodeSpec.props.attrs) {
		return nodeSpec;
	}

	// If main spec doesn't have attrs, look for secondary specs that extend this nodeType
	for (const [, secondarySpec] of Object.entries(allSpecs)) {
		if (Array.isArray(secondarySpec)) {
			const [baseNodeType, extendedSpec] = secondarySpec as ValidatorSpecNodeExtends;
			if (baseNodeType === nodeType && extendedSpec.props.attrs) {
				// Found a secondary spec that extends this nodeType and has attrs
				return {
					...nodeSpec,
					props: {
						...nodeSpec.props,
						...extendedSpec.props,
					},
					required: extendedSpec.required || nodeSpec.required,
				};
			}
		}
	}

	// Return main spec even if it doesn't have attrs
	return nodeSpec;
}

/**
 * Validate attributes against a single attribute group spec
 */
function validateAttrsAgainstGroup(
	attrs: AdfAttrs | undefined,
	attrsGroup: ValidatorSpecAttributesGroup,
	nodeType: string,
): { error?: string; isValid: boolean; repairedAttrs?: AdfAttrs } {
	// If no attrs provided, try to generate defaults for required attributes
	if (!attrs) {
		const defaultAttrs: AdfAttrs = {};
		const errors: string[] = [];

		for (const [attrName, attrRule] of Object.entries(attrsGroup.props)) {
			if (!attrRule.optional) {
				const defaultValue = generateDefaultValueForAttribute(attrRule, attrName, nodeType);
				if (defaultValue === null) {
					errors.push(
						`Required attribute '${attrName}' cannot have default value (has validatorFn)`,
					);
				} else {
					defaultAttrs[attrName] = defaultValue;
				}
			}
		}

		if (errors.length > 0) {
			return { isValid: false, error: errors.join('; ') };
		}

		return { isValid: true, repairedAttrs: defaultAttrs };
	}

	const repairedAttrs = { ...attrs };
	const errors: string[] = [];

	// Check all defined attributes in the rule
	for (const [attrName, attrRule] of Object.entries(attrsGroup.props)) {
		const attrValue = attrs[attrName];

		// Validate and potentially repair attribute value
		const validation = validateAndRepairAttributeValue(attrName, attrValue, attrRule, nodeType);
		if (validation.isValid) {
			repairedAttrs[attrName] = validation.repairedValue;
		}
		// Add validation error if attribute is not optional
		else if (!attrRule.optional) {
			errors.push(validation.error || `Invalid value for attribute '${attrName}'`);
		}
	}

	if (errors.length > 0) {
		return { isValid: false, error: errors.join('; ') };
	}

	return { isValid: true, repairedAttrs };
}

/**
 * Validate and repair a node's attributes
 */
export function validateAndRepairNodeAttrs(
	nodeType: string,
	attrs?: AdfAttrs,
): { error?: string; isValid: boolean; repairedAttrs?: AdfAttrs } {
	const spec = allSpecs[nodeType];
	if (!spec) {
		return { isValid: false, error: `Unknown node type: ${nodeType}` };
	}

	// Use resolveNodeSpec for main specs to handle attrs resolution
	const resolvedSpec = resolveNodeSpec(nodeType);
	if (!resolvedSpec) {
		return { isValid: false, error: `Unable to resolve spec for node type: ${nodeType}` };
	}
	const nodeSpec = resolvedSpec;

	const attrsRule = nodeSpec.props.attrs;
	if (!attrsRule) {
		// Node doesn't require attributes
		return { isValid: true, repairedAttrs: attrs || {} };
	}

	// Handle array of attribute groups (multiple possible schemas)
	if (Array.isArray(attrsRule)) {
		const errors: string[] = [];

		// Try each attribute group until one succeeds
		for (let i = 0; i < attrsRule.length; i++) {
			const attrsGroup = attrsRule[i];
			const result = validateAttrsAgainstGroup(attrs, attrsGroup, nodeType);
			/**
			 * If validateAttrsAgainstGroup has repaired attrs, then don't return.
			 * Check against next attrsGroup. If last group, then return.
			 */
			if (result.isValid && (i === attrsRule.length - 1 || isEqual(result.repairedAttrs, attrs))) {
				return result;
			} else {
				errors.push(result.error || 'Unknown validation error');
			}
		}

		// If none of the attribute groups worked, return combined errors
		return {
			isValid: false,
			error: `Node '${nodeType}' attrs don't match any valid schema: ${errors.join(' | ')}`,
		};
	}

	// Handle single attribute group
	return validateAttrsAgainstGroup(attrs, attrsRule, nodeType);
}
